import { useState, useEffect, useCallback } from 'react'
import Modal from '@/components/layout/Modal'

interface PasscodeReminderProps {
  userId: string
  hasBackupEmail: boolean
}

const DISMISS_KEY = (id: string) => `ssdl:passcode-banner-dismissed:${id}`
const LAST_MODAL_KEY = (id: string) => `ssdl:passcode-modal-last:${id}`
const FIRST_SEEN_KEY = (id: string) => `ssdl:first-seen:${id}`
const MODAL_DISMISS_COUNT_KEY = (id: string) => `ssdl:passcode-modal-count:${id}`

function daysSince(timestamp: number): number {
  return (Date.now() - timestamp) / (1000 * 60 * 60 * 24)
}

export default function PasscodeReminder({ userId, hasBackupEmail }: PasscodeReminderProps) {
  const [showBanner, setShowBanner] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [urlCopied, setUrlCopied] = useState(false)

  // Initialize first-seen timestamp
  useEffect(() => {
    if (!localStorage.getItem(FIRST_SEEN_KEY(userId))) {
      localStorage.setItem(FIRST_SEEN_KEY(userId), String(Date.now()))
    }
  }, [userId])

  // Show banner if not dismissed AND user hasn't set up email recovery.
  // Once they have a backup email they can recover via that flow, so the
  // "save your passcode or lose your data" warning is no longer accurate.
  useEffect(() => {
    if (hasBackupEmail) {
      setShowBanner(false)
      return
    }
    const dismissed = localStorage.getItem(DISMISS_KEY(userId))
    if (!dismissed) {
      setShowBanner(true)
    }
  }, [userId, hasBackupEmail])

  // Periodic modal reminder
  useEffect(() => {
    // Don't nag if they have a backup email — they can recover
    if (hasBackupEmail) return

    const firstSeen = parseInt(localStorage.getItem(FIRST_SEEN_KEY(userId)) || '0')
    const lastModal = parseInt(localStorage.getItem(LAST_MODAL_KEY(userId)) || '0')
    const dismissCount = parseInt(localStorage.getItem(MODAL_DISMISS_COUNT_KEY(userId)) || '0')

    if (!firstSeen) return

    const daysSinceFirst = daysSince(firstSeen)
    const daysSinceLastModal = lastModal ? daysSince(lastModal) : Infinity

    // Schedule: day 1, 3, 7, then every 14 days — but stop after 10 dismissals
    if (dismissCount >= 10) return

    let shouldShow = false
    if (daysSinceFirst >= 1 && !lastModal) shouldShow = true
    else if (daysSinceFirst >= 3 && daysSinceLastModal >= 2) shouldShow = true
    else if (daysSinceFirst >= 7 && daysSinceLastModal >= 7) shouldShow = true
    else if (daysSinceFirst >= 14 && daysSinceLastModal >= 14) shouldShow = true

    if (shouldShow) {
      // Delay modal so it doesn't flash on load
      const timer = setTimeout(() => setShowModal(true), 3000)
      return () => clearTimeout(timer)
    }
  }, [userId, hasBackupEmail])

  const copyPasscode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(userId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
      const input = document.createElement('input')
      input.value = userId
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [userId])

  const copyUrl = useCallback(async () => {
    const url = `${window.location.origin}/u/${userId}`
    try {
      await navigator.clipboard.writeText(url)
      setUrlCopied(true)
      setTimeout(() => setUrlCopied(false), 2000)
    } catch { /* ignore */ }
  }, [userId])

  const downloadKey = useCallback(() => {
    const url = `${window.location.origin}/u/${userId}`
    const content = `SSDL.app - Your Access Key
============================

Passcode: ${userId}

URL: ${url}

IMPORTANT: This is the ONLY way to access your drink log.
If you lose this passcode and haven't set up a backup email,
your data cannot be recovered.

Keep this file somewhere safe.
`
    const blob = new Blob([content], { type: 'text/plain' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'ssdl-access-key.txt'
    a.click()
    URL.revokeObjectURL(a.href)
  }, [userId])

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISS_KEY(userId), 'true')
    setShowBanner(false)
  }, [userId])

  const dismissModal = useCallback(() => {
    localStorage.setItem(LAST_MODAL_KEY(userId), String(Date.now()))
    const count = parseInt(localStorage.getItem(MODAL_DISMISS_COUNT_KEY(userId)) || '0')
    localStorage.setItem(MODAL_DISMISS_COUNT_KEY(userId), String(count + 1))
    setShowModal(false)
  }, [userId])

  return (
    <>
      {/* Persistent banner */}
      {showBanner && (
        <div className="card border-[var(--accent-orange)] bg-[var(--accent-orange)]/10 mb-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-[var(--accent-orange)] shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[var(--accent-orange)]">
                Save your passcode now — it's the only way back in
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                No account, no email recovery (unless you set one up). Lose it and your data is gone forever.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                <button onClick={copyPasscode} className="text-xs px-3 py-1.5 rounded bg-[var(--accent-orange)] text-black font-medium hover:opacity-90 transition-opacity">
                  {copied ? 'Copied!' : 'Copy Passcode'}
                </button>
                <button onClick={copyUrl} className="text-xs px-3 py-1.5 rounded bg-[var(--bg-card)] text-[var(--text-primary)] hover:opacity-80 transition-opacity">
                  {urlCopied ? 'Copied!' : 'Copy URL'}
                </button>
                <button onClick={downloadKey} className="text-xs px-3 py-1.5 rounded bg-[var(--bg-card)] text-[var(--text-primary)] hover:opacity-80 transition-opacity">
                  Download Key File
                </button>
              </div>
            </div>
            <button onClick={dismissBanner} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] shrink-0" aria-label="Dismiss">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Periodic modal reminder */}
      <Modal isOpen={showModal} onClose={dismissModal} title="Don't Lose Access to Your Data" size="sm">
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--accent-orange)]/20 border border-[var(--accent-orange)]/40">
            <svg className="w-8 h-8 text-[var(--accent-orange)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div>
              <p className="font-bold text-[var(--accent-orange)]">Your passcode is not saved anywhere</p>
              <p className="text-sm text-[var(--text-secondary)]">If you lose it, your data is gone forever</p>
            </div>
          </div>

          <p className="text-sm text-[var(--text-secondary)]">
            SSDL has no accounts and no way to look you up. Your passcode is the <strong className="text-[var(--text-primary)]">only key</strong> to your data. Please save it now:
          </p>

          <div className="space-y-2">
            <button onClick={copyPasscode} className="w-full btn btn-primary flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
              </svg>
              {copied ? 'Copied!' : 'Copy Passcode to Clipboard'}
            </button>
            <button onClick={downloadKey} className="w-full btn btn-secondary flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download Key File
            </button>
          </div>

          <p className="text-xs text-[var(--text-secondary)] text-center">
            Tip: Set up a <strong>backup email</strong> in Settings to stop these reminders and enable key recovery.
          </p>
        </div>
      </Modal>
    </>
  )
}
